import { ErrorState, Spinner, EmptyState } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import { fmtValue, fmtPnl } from '../../broker/bookUtils';
import EditableCell from './EditableCell';

// P&L cell — same green/red semantics as the global Paper Position Book (theme bull/bear tokens).
function PnlCell({ value }) {
  const num = Number(value);
  const cls = value == null || value === '' ? 'text-txt-secondary' : num > 0 ? 'text-bull' : num < 0 ? 'text-bear' : 'text-txt-secondary';
  return <span className={cn('font-medium', cls)}>{fmtPnl(value)}</span>;
}

// Active Target / Active SL cell (E2.2). Editable only when an ACTIVE risk trigger exists; the value
// comes ONLY from the trigger (never the watchlist row). Enabled side shows the trigger price; a
// disabled side shows the "Off" placeholder. Clearing the input disables that side (handler sends null).
function ActiveRiskCell({ row, side, enabled, price, onEditActiveRisk, inputWidth }) {
  // No current trigger (and no self-heal) → nothing to show or edit.
  if (!row.canEditActiveRisk && !row.hasCurrentRisk) {
    return <span className="text-txt-muted" title="No active risk trigger for this position">—</span>;
  }
  // Current trigger exists but editing is locked (PROCESSING = in-flight risk exit) → read-only value.
  if (!row.canEditActiveRisk) {
    return (
      <span className="text-txt-secondary tabular-nums" title="Risk exit in progress">
        {enabled && price != null ? fmtValue(price) : 'Off'}
      </span>
    );
  }
  return (
    <EditableCell
      type="number"
      inputMode="decimal"
      value={enabled ? price : ''}
      placeholder="Off"
      width={inputWidth}
      onCommit={(val) => onEditActiveRisk(row, side, val)}
    />
  );
}

// Sizing wrap for ActiveRiskCell's EditableCell inside a mobile card — same arbitrary
// child-selector technique used in WatchlistMobileRows.jsx; no EditableCell.jsx change.
const RISK_INPUT_WRAP = '[&>input]:w-full [&>input]:px-1.5 [&>input]:py-1 [&>input]:text-sm [&>input]:tabular-nums';

function StatusBadge({ status }) {
  const isOpen = String(status).toUpperCase() === 'OPEN';
  return (
    <span className={cn('badge border', isOpen ? 'bg-bull/10 border-bull/30 text-bull' : 'bg-bg-hover border-bg-border text-txt-muted')}>
      {fmtValue(status)}
    </span>
  );
}

// Solid-blue primary Sync button — placed immediately before Exit, both desktop and mobile.
// Row-level loading: only the clicked row (row.id === syncingId) shows the spinner/disabled state.
function SyncButton({ row, syncingId, onSync, className }) {
  const busy = row.id === syncingId;
  return (
    <button
      type="button"
      disabled={busy}
      aria-busy={busy}
      title="Sync this position against the broker"
      onClick={() => onSync(row)}
      className={cn(
        'rounded-md text-2xs font-semibold border border-info bg-info text-white hover:bg-info/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
    >
      {busy ? 'Syncing…' : 'Sync'}
    </button>
  );
}

// Mobile card presentation of one position row — same data/handlers as the desktop
// table's columns, just laid out for touch instead of a scrollable table row.
function PositionCard({ row, exitingId, onExit, syncingId, onSync, syncEnabled, onEditActiveRisk }) {
  const busy = row.id === exitingId || row.exitProcessing;
  const isClosed = row.isClosed || Number(row.netQty) === 0;
  const canSync = syncEnabled && row.canExit && !isClosed;
  return (
    <div className="rounded-md border border-bg-border bg-bg-surface/40 p-2 mb-1.5">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="font-medium text-txt-primary truncate min-w-0">{fmtValue(row.symbol)}</span>
        <StatusBadge status={row.status} />
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-0.5">
        {row.product != null && row.product !== '' && <span>{fmtValue(row.product)} · </span>}
        Net Qty: <span className="text-txt-secondary">{fmtValue(row.netQty)}</span>
        {' · '}Buy: <span className="text-txt-secondary">{fmtValue(row.buyAvg)}</span>
        {' · '}Sell: <span className="text-txt-secondary">{fmtValue(row.sellAvg)}</span>
      </div>

      <div className="text-2xs font-mono text-txt-muted mb-1">
        Realized <PnlCell value={row.realizedPnl} />
        {' · '}Unrealized <PnlCell value={row.unrealizedPnl} />
      </div>

      {!isClosed && (
        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 mb-1.5">
          <span className="text-xs font-mono text-txt-muted shrink-0">
            LTP <span className="text-txt-primary font-medium">{fmtValue(row.ltp)}</span>
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-2xs font-medium text-txt-label shrink-0">TP</span>
            <div className={RISK_INPUT_WRAP}>
              <ActiveRiskCell row={row} side="target" enabled={row.activeTargetEnabled} price={row.activeTargetPrice} onEditActiveRisk={onEditActiveRisk} inputWidth="104px" />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-2xs font-medium text-txt-label shrink-0">SL</span>
            <div className={RISK_INPUT_WRAP}>
              <ActiveRiskCell row={row} side="sl" enabled={row.activeStopLossEnabled} price={row.activeStopLossPrice} onEditActiveRisk={onEditActiveRisk} inputWidth="104px" />
            </div>
          </div>
        </div>
      )}

      {!isClosed && row.canExit ? (
        <div className="flex items-center gap-2 mt-2.5">
          {canSync && (
            <SyncButton
              row={row}
              syncingId={syncingId}
              onSync={onSync}
              className="flex-1 min-h-[44px] text-xs"
            />
          )}
          <button
            type="button"
            disabled={busy}
            title={row.exitProcessing ? 'Risk exit in progress' : undefined}
            onClick={() => onExit(row)}
            className="flex-1 min-h-[44px] rounded-md text-xs font-semibold border border-warn/30 bg-warn/10 text-warn hover:bg-warn/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Exiting…' : 'EXIT'}
          </button>
        </div>
      ) : !isClosed ? (
        <p className="text-2xs text-txt-muted text-center mt-2.5" title="Cannot exit: matching watchlist row not found.">
          Exit unavailable
        </p>
      ) : null}
    </div>
  );
}

// Open Positions tab — columns mirror the global Broker → Position Book (Paper) page (deployment-filtered),
// plus a Status column and the Manual EXIT action. Rows come from bookUtils.normalizePositionRow (+ live
// LTP / unrealized merge) in ManualStrategyCard. Quantities are shown exactly as the backend returns them.
export default function PositionsTable({
  positions = [],
  loading = false,
  error = null,
  onRetry,
  exitingId = null,
  onExit,
  syncingId = null,
  onSync,
  syncEnabled = false,
  onEditActiveRisk,
  totalPnl = 0,
  exitAllEnabled = false,
  exitAllTitle = '',
  onExitAllClick,
}) {
  const totalPnlCls = totalPnl > 0 ? 'text-bull' : totalPnl < 0 ? 'text-bear' : 'text-txt-secondary';
  // Desktop-only compact headers (abbreviated to keep every header on one line at typical
  // desktop widths) with a native `title` tooltip spelling out the abbreviation. `align` drives
  // left/right text alignment; `mobileHidden`/`hideUntilXl` preserve the existing progressive
  // column disclosure (hidden below `sm`/`xl`) — inert below `lg` since this table only renders
  // in the `hidden lg:block` desktop branch, but kept for exact parity with prior behavior.
  const columns = [
    { key: 'symbol', label: 'Symbol', align: 'left', render: (v) => <span className="text-txt-primary font-medium">{fmtValue(v)}</span> },
    { key: 'product', label: 'Prod', title: 'Product', align: 'left', width: '4rem', render: (v) => fmtValue(v), mobileHidden: true, hideUntilXl: true },
    { key: 'buyQty', label: 'Buy Qty', align: 'right', render: (v) => fmtValue(v) },
    { key: 'buyAvg', label: 'Buy Avg', align: 'right', render: (v) => fmtValue(v), mobileHidden: true, hideUntilXl: true },
    { key: 'sellQty', label: 'Sell Qty', align: 'right', render: (v) => fmtValue(v) },
    { key: 'sellAvg', label: 'Sell Avg', align: 'right', render: (v) => fmtValue(v), mobileHidden: true, hideUntilXl: true },
    { key: 'netQty', label: 'Net Qty', align: 'right', render: (v) => fmtValue(v) },
    { key: 'ltp', label: 'LTP', align: 'right', width: '5.5rem', render: (v) => fmtValue(v) },
    { key: 'realizedPnl', label: 'Rld P&L', title: 'Realized P&L', align: 'right', render: (v) => <PnlCell value={v} /> },
    { key: 'unrealizedPnl', label: 'Unrld P&L', title: 'Unrealized P&L', align: 'right', render: (v) => <PnlCell value={v} /> },
    // E2.2 — Active risk trigger levels (editable). Sourced from the risk-triggers API, NOT the watchlist.
    {
      key: 'activeTarget',
      label: 'Target',
      title: 'Active Target',
      align: 'right',
      render: (_v, row) => (
        <ActiveRiskCell row={row} side="target" enabled={row.activeTargetEnabled} price={row.activeTargetPrice} onEditActiveRisk={onEditActiveRisk} />
      ),
    },
    {
      key: 'activeStopLoss',
      label: 'SL',
      title: 'Active Stop Loss',
      align: 'right',
      render: (_v, row) => (
        <ActiveRiskCell row={row} side="sl" enabled={row.activeStopLossEnabled} price={row.activeStopLossPrice} onEditActiveRisk={onEditActiveRisk} />
      ),
    },
    { key: 'status', label: 'Status', align: 'left', width: '4.5rem', render: (v) => <StatusBadge status={v} /> },
    {
      key: 'action',
      label: 'Action',
      align: 'left',
      render: (_v, row) => {
        // Full square-off only. Disabled when the position can't be mapped to a watchlist row.
        if (!row.canExit) {
          return <span className="text-txt-muted" title="Cannot exit: matching watchlist row not found.">—</span>;
        }
        const busy = row.id === exitingId || row.exitProcessing;
        return (
          <div className="flex items-center gap-1.5">
            {syncEnabled && !row.isClosed && (
              <SyncButton row={row} syncingId={syncingId} onSync={onSync} className="mt-action px-2.5 py-1" />
            )}
            <button
              type="button"
              disabled={busy}
              title={row.exitProcessing ? 'Risk exit in progress' : undefined}
              onClick={() => onExit(row)}
              className="mt-action px-2.5 py-1 rounded-md text-2xs font-semibold border border-warn/30 bg-warn/10 text-warn hover:bg-warn/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Exiting…' : 'EXIT'}
            </button>
          </div>
        );
      },
    },
  ];

  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  return (
    <>
      {/* Desktop — compact local table (not the shared ui/DataTable) so header abbreviations,
          tooltips, alignment, and tightened row/header padding stay scoped to this Positions
          view only; no other DataTable-based screen is affected. Bounded to ~6 data rows (a
          single overflow-auto container handles vertical + horizontal scroll together, so a 7th+
          row scrolls internally without any page-level horizontal overflow); the sticky, shaded
          header (bg-bg-hover — visibly distinct from the plain row background, opaque so
          scrolling row text never shows through) stays pinned during that scroll. Rows are never
          fetched/dropped for this — positions.length is unaffected. */}
      <div className="hidden lg:block">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size={20} />
          </div>
        ) : !positions.length ? (
          <EmptyState title="No positions" />
        ) : (
          <div className="overflow-auto max-h-[14.5rem]">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      title={col.title}
                      className={cn(
                        'sticky top-0 z-10 bg-bg-hover border-b border-bg-border',
                        'py-1.5 px-2.5 text-txt-label uppercase tracking-widest text-2xs font-semibold whitespace-nowrap',
                        col.align === 'right' ? 'text-right' : 'text-left',
                        col.mobileHidden && 'hidden sm:table-cell',
                        col.hideUntilXl && 'hidden xl:table-cell',
                      )}
                      style={{ width: col.width }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {positions.map((row, i) => (
                  <tr key={row.id ?? i} className="border-b border-bg-border/50">
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'py-2 px-2.5 text-txt-secondary font-mono',
                          col.align === 'right' ? 'text-right' : 'text-left',
                          col.mobileHidden && 'hidden sm:table-cell',
                          col.hideUntilXl && 'hidden xl:table-cell',
                        )}
                      >
                        {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="lg:hidden">
        <div className="sticky top-0 z-10 -mx-1 px-1 py-2 mb-2 bg-bg-surface border-b border-bg-border flex items-center justify-between gap-3">
          <span className="text-2xs font-mono">
            <span className="text-txt-label">Total P&L: </span>
            <span className={cn('font-medium', totalPnlCls)}>₹{fmtPnl(totalPnl)}</span>
          </span>
          <button
            type="button"
            disabled={!exitAllEnabled}
            title={exitAllTitle}
            onClick={onExitAllClick}
            className="btn-danger text-xs disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Exit All
          </button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={20} />
          </div>
        ) : !positions.length ? (
          <EmptyState title="No positions" />
        ) : (
          positions.map((row, i) => (
            <PositionCard
              key={row.id ?? i}
              row={row}
              exitingId={exitingId}
              onExit={onExit}
              syncingId={syncingId}
              onSync={onSync}
              syncEnabled={syncEnabled}
              onEditActiveRisk={onEditActiveRisk}
            />
          ))
        )}
      </div>
    </>
  );
}
